// Rollen van personen, afgeleid uit hun functie-velden (Airtable `Functie`, multipleSelects).
//
// Joris (25 sep): "Ik mis regisseurs en cameramannen om te filteren." Het Rooster kende
// alleen Editors/Animators/Illustrators; de rest viel onder "Overig". De rollen komen nu
// uit de data: elke functie van een persoon wordt op één rol gelegd, in een vaste volgorde.
//
// Valkuil (CLAUDE.md, "multipleSelects"): de functie komt als array (["Editor", "Animator"])
// óf als komma-string ("Editor, Animator") binnen, afhankelijk van de route. Altijd eerst
// normaliseren met `functiesVan`.
//
// Gedeeld: het Rooster (Weergave-menu, groepen) gebruikt dit; Capaciteit kan dezelfde
// definitie gebruiken voor zijn Functie-keuzelijst.

/** Vaste volgorde; `patronen` worden per losse functie getoetst (eerste treffer wint). */
export const ROLLEN = [
  { key: 'Editor', label: 'Editors', patronen: [/editor/, /montage/, /colou?r\s*grad/, /\bedit/] },
  { key: 'Animator', label: 'Animators', patronen: [/animat/, /motion/] },
  { key: 'Illustrator', label: 'Illustrators', patronen: [/illustrat/] },
  // Camera vóór regie: "Director of Photography" is camera, geen regisseur.
  { key: 'Camera', label: 'Cameramensen', patronen: [/camera/, /\bdop\b/, /director of photography/, /cinematogra/] },
  { key: 'Regisseur', label: 'Regisseurs', patronen: [/regisseu/, /\bregie\b/] },
  { key: 'Projectleider', label: 'Projectleiders', patronen: [/projectleid/, /project\s*manager/, /\bpl\b/] },
  { key: 'Producer', label: 'Producers', patronen: [/producer/, /producent/] },
];

export const OVERIG = 'Overig';

/** Rol-sleutel → label, incl. Overig. */
export const ROL_LABEL = Object.fromEntries([...ROLLEN.map((r) => [r.key, r.label]), [OVERIG, 'Overig']]);

/** Alle functies van een persoon als schone lijst, of het veld nu een array of een string is. */
export function functiesVan(p) {
  const raw = p?.function ?? p?.Functie ?? p?.functie ?? '';
  const lijst = Array.isArray(raw) ? raw : String(raw).split(/[,;]/);
  return lijst.map((f) => String(f ?? '').trim()).filter(Boolean);
}

/** Welke rol hoort bij één functie ("Cameraman" → 'Camera'), of null. */
export function rolVanFunctie(functie) {
  const f = String(functie || '').toLowerCase();
  if (!f) return null;
  const rol = ROLLEN.find((r) => r.patronen.some((re) => re.test(f)));
  return rol ? rol.key : null;
}

/** Alle rollen van een persoon, in de volgorde van zijn functies, zonder dubbelen. */
export function rollenVan(p) {
  const uit = [];
  functiesVan(p).forEach((f) => {
    const k = rolVanFunctie(f);
    if (k && !uit.includes(k)) uit.push(k);
  });
  return uit;
}

/** Primaire rol = die van de eerste herkende functie; anders Overig. */
export const primaireRol = (p) => rollenVan(p)[0] || OVERIG;

/** Heeft deze persoon (één van) deze rollen? Lege filter = iedereen. */
export function heeftRol(p, filter = []) {
  if (!filter || !filter.length) return true;
  const eigen = rollenVan(p);
  if (filter.includes(OVERIG) && !eigen.length) return true;
  return eigen.some((k) => filter.includes(k));
}

/**
 * Rollen die bij minstens één persoon voorkomen, in de vaste volgorde, met aantal
 * (iemand met twee rollen telt bij allebei). `altijd` = rollen die zichtbaar blijven,
 * ook zonder personen (bv. een aangevinkte filter).
 */
export function rolTellingen(persons, { altijd = [] } = {}) {
  const tel = {};
  (persons || []).forEach((p) => rollenVan(p).forEach((k) => { tel[k] = (tel[k] || 0) + 1; }));
  return ROLLEN
    .filter((r) => tel[r.key] || altijd.includes(r.key))
    .map((r) => ({ key: r.key, label: r.label, aantal: tel[r.key] || 0 }));
}
